// AuthCallbackPage.tsx
import { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useUserRegistration } from '../../hooks/useUserRegistration';
import { useAuthStore } from '../../stores/auth.store';

export function AuthCallbackPage() {
  const {
    handleRedirectCallback,
    isLoading: isAuth0Loading,
    error: auth0Error,
    user: auth0User,
  } = useAuth0();
  const isProfileLoading = useAuthStore((state: any) => state.isLoadingProfile);
  const {
    registerUser,
    checkUserExists,
    isLoading: isRegistering,
    error: registrationError,
  } = useUserRegistration();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isHandlingCallback, setIsHandlingCallback] = useState(false);

  const handleAuthCallback = useCallback(async () => {
    if (!auth0User?.sub || !auth0User?.email) {
      return;
    }

    try {
      let appState;
      try {
        const handleRedirectCallbackRes = await handleRedirectCallback();
        appState = handleRedirectCallbackRes.appState;
      } catch (err) {
        console.error('Auth callback error:', err);
      }

      // Check if user exists
      const userExists = await checkUserExists(auth0User.email, auth0User.sub);

      if (!userExists) {
        // Register new user
        await registerUser({
          firstName:
            auth0User.given_name || auth0User.name?.split(' ')[0] || '',
          lastName:
            auth0User.family_name ||
            auth0User.name?.split(' ').slice(1).join(' ') ||
            '',
        });
      }

      // Navigate to the intended destination or dashboard
      navigate(appState?.returnTo || '/dashboard', { replace: true });
    } catch (err) {
      console.error('Auth callback error:', err);
      setError(
        err instanceof Error ? err.message : 'An unexpected error occurred'
      );
    }
  }, [
    auth0User,
    handleRedirectCallback,
    checkUserExists,
    registerUser,
    navigate,
  ]);

  useEffect(() => {
    if (!isHandlingCallback && auth0User && !isAuth0Loading) {
      setIsHandlingCallback(true);
      handleAuthCallback().finally(() => {
        setIsHandlingCallback(false);
      });
    }
  }, [isAuth0Loading, auth0User, handleAuthCallback, isHandlingCallback]);

  // Show loading state during any async operation
  if (
    isAuth0Loading ||
    isProfileLoading ||
    isRegistering ||
    isHandlingCallback
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-xl font-semibold">
            Setting up your account...
          </div>
          <div className="text-gray-500">This may take a few moments</div>
        </div>
      </div>
    );
  }

  // Error states
  if (error || auth0Error || registrationError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-xl font-semibold text-red-600">Oops!</div>
          <div className="text-gray-600">
            {error ||
              auth0Error?.message ||
              registrationError?.message ||
              'An unexpected error occurred'}
          </div>
          <button
            onClick={() => navigate('/')}
            className="mt-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
}
